import { useEffect, useRef, useState, type FormEvent } from "react";
import { Pencil, Power, Trash2 } from "lucide-react";
import { canManageSports } from "../lib/access";
import { ApiError } from "../lib/api/client";
import { createSport, deleteSport, listSports, toggleSportStatus, updateSport } from "../lib/api/endpoints";
import { useSession } from "../lib/session/SessionContext";
import { C, fieldStyle } from "../theme";
import type { CreateSportRequest, SportDto } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { ConfirmDialog } from "./ConfirmDialog";
import { FieldInput } from "./Field";
import { Label } from "./Label";

const SPORT_PAGE_SIZE = 20;

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.detail ?? error.message : fallback;
}

function SportForm({ initialSport, submitting, submitError, onSubmit, onCancel }: { initialSport?: SportDto | null; submitting: boolean; submitError: string | null; onSubmit: (payload: CreateSportRequest) => void; onCancel: () => void }) {
  const [name, setName] = useState(initialSport?.name ?? "");
  const [error, setError] = useState<string | null>(null);
  const isEditing = Boolean(initialSport);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      setError("Informe o nome da modalidade");
      return;
    }
    onSubmit(isEditing ? { name: name.trim() } : { name: name.trim(), isActive: true });
  };

  return <div role="dialog" aria-modal="true" aria-labelledby="sport-form-title" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
    <Card style={{ width: "100%", maxWidth: 480 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}><div id="sport-form-title"><Label>{isEditing ? "Editar modalidade" : "Nova modalidade"}</Label></div><Button variant="ghost" onClick={onCancel} disabled={submitting} ariaLabel="Fechar">X</Button></div>
      <form onSubmit={submit} noValidate>
        <label htmlFor="sport-name" style={{ display: "block", color: C.gray, fontSize: 12, fontWeight: 600, margin: "16px 0 4px" }}>Nome da modalidade</label>
        <FieldInput id="sport-name" value={name} maxLength={120} disabled={submitting} onChange={event => setName(event.target.value)} />
        {error && <div role="alert" style={{ color: C.red, fontSize: 11.5, marginTop: 8 }}>{error}</div>}
        {submitError && <div role="alert" style={{ color: C.red, fontSize: 11.5, marginTop: 8 }}>{submitError}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}><Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button><Button type="submit" disabled={submitting}>{submitting ? "Salvando..." : isEditing ? "Salvar modalidade" : "Criar modalidade"}</Button></div>
      </form>
    </Card>
  </div>;
}

export function SportManagementTab() {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const canManage = canManageSports(activeTenant?.roleSlug ?? null);
  const mutationRef = useRef(false);
  const [sports, setSports] = useState<SportDto[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formSport, setFormSport] = useState<SportDto | null | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<SportDto | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SportDto | null>(null);
  const [processing, setProcessing] = useState(false);
  const active = statusFilter === "all" ? undefined : statusFilter === "active";

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setSports(null);
    setLoadError(null);
    listSports(token, { page, pageSize: SPORT_PAGE_SIZE, isActive: active }).then(result => {
      if (!cancelled) {
        setSports(result.items);
        setTotalCount(result.totalCount);
      }
    }).catch(error => {
      if (!cancelled) {
        setSports([]);
        setLoadError(errorMessage(error, "Falha ao carregar modalidades."));
      }
    });
    return () => { cancelled = true; };
  }, [token, page, active, reloadKey]);

  const submitSport = async (payload: CreateSportRequest) => {
    if (!token || mutationRef.current) return;
    mutationRef.current = true;
    setSubmitting(true);
    setActionError(null);
    try {
      const saved = formSport ? await updateSport(token, formSport.id, { name: payload.name }) : await createSport(token, payload);
      setSports(current => formSport ? current?.map(sport => sport.id === saved.id ? saved : sport) ?? [] : [saved, ...(current ?? [])].slice(0, SPORT_PAGE_SIZE));
      setTotalCount(current => formSport ? current : current + 1);
      setFormSport(undefined);
      setSuccess(formSport ? "Modalidade atualizada com sucesso." : "Modalidade criada com sucesso.");
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao salvar modalidade."));
    } finally {
      mutationRef.current = false;
      setSubmitting(false);
    }
  };

  const confirmStatus = async () => {
    if (!token || !pendingStatus || mutationRef.current) return;
    mutationRef.current = true;
    setProcessing(true);
    setActionError(null);
    try {
      const saved = await toggleSportStatus(token, pendingStatus.id, !pendingStatus.isActive);
      setSports(current => current?.map(sport => sport.id === saved.id ? saved : sport) ?? []);
      setPendingStatus(null);
      setSuccess(`Modalidade ${saved.isActive ? "ativada" : "desativada"} com sucesso.`);
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao alterar o status da modalidade."));
    } finally {
      mutationRef.current = false;
      setProcessing(false);
    }
  };

  const confirmDelete = async () => {
    if (!token || !pendingDelete || mutationRef.current) return;
    mutationRef.current = true;
    setProcessing(true);
    setActionError(null);
    try {
      await deleteSport(token, pendingDelete.id);
      setSports(current => current?.filter(sport => sport.id !== pendingDelete.id) ?? []);
      setTotalCount(current => Math.max(0, current - 1));
      setPendingDelete(null);
      setSuccess("Modalidade excluída com sucesso.");
    } catch (error) {
      setActionError(errorMessage(error, "Falha ao excluir modalidade."));
    } finally {
      mutationRef.current = false;
      setProcessing(false);
    }
  };

  return <div style={{ display: "grid", gap: 14 }}>
    {success && <div role="status" style={{ color: C.ink, background: C.limeSoft, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{success}</div>}
    {loadError && <div role="alert" style={{ color: C.red, background: C.white, border: `1px solid ${C.faint}`, borderRadius: 10, padding: 12, fontSize: 12.5 }}>{loadError} <Button variant="ghost" onClick={() => setReloadKey(value => value + 1)}>Tentar novamente</Button></div>}
    {actionError && !formSport && <div role="alert" style={{ color: C.red, fontSize: 12 }}>{actionError}</div>}
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}><div><Label>Gestão de modalidades</Label><div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>Cadastre as modalidades disponíveis para a operação do CT.</div></div>{canManage ? <Button onClick={() => { setActionError(null); setFormSport(null); }}>+ Nova modalidade</Button> : <span style={{ color: C.light, background: C.bg, borderRadius: 12, padding: "5px 9px", fontSize: 11.5 }}>Somente leitura</span>}</div>
      <div style={{ marginTop: 14 }}><select aria-label="Filtrar modalidades por status" value={statusFilter} onChange={event => { setStatusFilter(event.target.value); setPage(1); }} style={fieldStyle}><option value="all">Todos os status</option><option value="active">Ativas</option><option value="inactive">Inativas</option></select></div>
      {sports === null && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Carregando modalidades...</div>}
      {sports?.length === 0 && !loadError && <div style={{ color: C.light, padding: "22px 0", fontSize: 13 }}>Nenhuma modalidade encontrada.</div>}
      {sports && sports.length > 0 && <div style={{ display: "grid", gap: 8, marginTop: 14 }}>{sports.map(sport => <div key={sport.id} style={{ border: `1px solid ${C.faint}`, borderRadius: 10, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}><div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}><b style={{ color: C.ink, fontSize: 13.5 }}>{sport.name}</b><span style={{ color: sport.isActive ? C.ink : C.light, background: sport.isActive ? C.limeSoft : C.bg, borderRadius: 10, padding: "2px 7px", fontSize: 10.5, fontWeight: 700 }}>{sport.isActive ? "Ativa" : "Inativa"}</span></div>{canManage && <div style={{ display: "flex", gap: 8 }}><Button variant="ghost" ariaLabel={`Editar ${sport.name}`} title="Editar" onClick={() => { setActionError(null); setFormSport(sport); }}><Pencil size={16} /></Button><Button variant="ghost" ariaLabel={`${sport.isActive ? "Desativar" : "Ativar"} ${sport.name}`} title={sport.isActive ? "Desativar" : "Ativar"} onClick={() => setPendingStatus(sport)}><Power size={16} /></Button><Button variant="ghost" ariaLabel={`Excluir ${sport.name}`} title="Excluir" onClick={() => setPendingDelete(sport)}><Trash2 size={16} /></Button></div>}</div>)}</div>}
      {totalCount > SPORT_PAGE_SIZE && <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, alignItems: "center" }}><span style={{ color: C.light, fontSize: 12 }}>Página {page} de {Math.ceil(totalCount / SPORT_PAGE_SIZE)}</span><div style={{ display: "flex", gap: 8 }}><Button variant="outline" disabled={page === 1} onClick={() => setPage(value => value - 1)}>Anterior</Button><Button variant="outline" disabled={page >= Math.ceil(totalCount / SPORT_PAGE_SIZE)} onClick={() => setPage(value => value + 1)}>Próxima</Button></div></div>}
    </Card>
    {formSport !== undefined && <SportForm initialSport={formSport} submitting={submitting} submitError={actionError} onSubmit={submitSport} onCancel={() => !submitting && setFormSport(undefined)} />}
    {pendingStatus && <ConfirmDialog title={`${pendingStatus.isActive ? "Desativar" : "Ativar"} modalidade`} confirmLabel={`${pendingStatus.isActive ? "Desativar" : "Ativar"} modalidade`} destructive={pendingStatus.isActive} busy={processing} onConfirm={confirmStatus} onCancel={() => !processing && setPendingStatus(null)}>Deseja {pendingStatus.isActive ? "desativar" : "ativar"} {pendingStatus.name}?</ConfirmDialog>}
    {pendingDelete && <ConfirmDialog title="Excluir modalidade" confirmLabel="Excluir modalidade" busy={processing} onConfirm={confirmDelete} onCancel={() => !processing && setPendingDelete(null)}>Deseja excluir {pendingDelete.name}? Esta ação não pode ser desfeita.</ConfirmDialog>}
  </div>;
}
